import { useMemo } from 'react';
import moment from 'moment';
import { AbsenceEvent } from './types'; // Adjust to your types file

export type EmployeeEventMap = Record<string, Record<string, AbsenceEvent>>;

const SUPPRESSION_PRIORITIES: Record<string, number> = {
  remote: 10,
  businesstrip: 20,
};

/**
 * Helper function to safely get the priority of an event.
 * If the event's kind is not mapped, it returns large value, so that we map only weaker events.
 */
const getEventPriority = (event: AbsenceEvent | undefined): number => {
  if (!event || !event.kind) return 1000;
  return SUPPRESSION_PRIORITIES[event.kind] ?? 1000;
};

/**
 * Pre-processes a flat list of AbsenceEvents into a nested dictionary:
 * { [owner_id]: { [YYYY-MM-DD]: event } }
 * Handles overlapping events based on SUPPRESSION_PRIORITIES:
 * - Higher priority wins.
 * - If priorities are exactly equal, the last one in the array wins.
 */
export const useAbsenceEventBuckets = (events: AbsenceEvent[], startDate: string, endDate: string): EmployeeEventMap => {
  return useMemo(() => {
    const buckets: EmployeeEventMap = {};
    if (!events) return buckets;

    const startBoundary = moment(startDate);
    const endBoundary = moment(endDate);

    events.forEach(event => {
      // Ignore cancelled events
      // if (event.cancelled_at) return;

      const eventStart = moment(event.start_date);
      const eventEnd = moment(event.end_date);

      // Only process if it overlaps with our calendar view
      if (eventEnd.isBefore(startBoundary, 'day') || eventStart.isAfter(endBoundary, 'day')) {
        return;
      }

      if (!buckets[event.owner_id]) {
        buckets[event.owner_id] = {};
      }

      const newEventPriority = getEventPriority(event);

      // Populate every day this event covers
      let current = eventStart.clone();
      while (current.isSameOrBefore(eventEnd, 'day')) {
        const dateStr = current.format('YYYY-MM-DD');
        const existingEvent = buckets[event.owner_id][dateStr];

        if (existingEvent) {
          const existingPriority = getEventPriority(existingEvent);

          // SUPPRESSION LOGIC:
          // We overwrite IF the new event has a strictly HIGHER priority
          // OR if the priorities are EQUAL (falling back to "last one wins").
          if (newEventPriority < existingPriority || (!!event.cancelled_at && !existingEvent.cancelled_at)) {
            current.add(1, 'day');
            continue; // Skip overwriting
          }
        }

        // If there is no existing event, or we survived suppression, store it
        buckets[event.owner_id][dateStr] = event;
        current.add(1, 'day');
      }
    });

    return buckets;
  }, [events, startDate, endDate]);
};